import React, { Fragment, useCallback, useState } from 'react';
import CommonPopUp from './common-popup';
import { Methods } from '../../core/utility';
import { UploadService, LocalService } from '../../core/services';
import LinearProgress from '@mui/material/LinearProgress';
import * as gtUtil from "../../core/utility";
import { filterProps } from 'framer-motion';
import MaterialSymbol from '../icons/material-symbol';
import Cropper from 'react-easy-crop';
import { Tooltip } from '@mui/material';

const methods: Methods = new Methods();
const localService = new LocalService();

interface FileUploadPopupProps {
    openModal: boolean;
    onClose: (closed: boolean) => void;
    acceptedFileTypes: string;
    onSend: (data: any) => void;
    path: string;
    fileMinimumSize?: number; // in KB
    fileMaximumSize?: number;
    coverImage?: boolean,
    coverImageDimensionText?: string
}

const FileUploadPopup: React.FC<FileUploadPopupProps> = ({
    openModal,
    onClose,
    onSend,
    acceptedFileTypes,
    path,
    fileMinimumSize = 0,
    coverImage,
    coverImageDimensionText
}) => {
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [src, setSelectedImage] = useState<any>(null);
    const [error, setError] = useState({ status: false, message: '' });

    // Define size limits in KB
    const fileSizeLimits: Record<string, number> = {
        'video/mp4': 150 * 1024, // 150 MB
        'image/jpeg': 2 * 1024,  // 2 MB
        'image/png': 2 * 1024,   // 2 MB
        'image/jpg': 2 * 1024,   // 2 MB
        'application/pdf': 500,  // 500 KB
        'application/msword': 500, // 500 KB
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 500, // 500 KB
    };

    const handleClosePreviewImage = () => {
        setFile(null);
    };

    // Upload file to server
    const uploadFile = () => {
        if (file != null && src != null) {
            setLoading(true);
            const form = new FormData();
            form.append('file', file, file.name);
            form.append('path', path);

            UploadService.UploadMedia(form)
                .then((res) => {
                    if (res.status === gtUtil.Constants.SUCCESS) {
                        localService.toastify(res.info, 'success', 1000);
                        onSend(res?.data?.rows);
                        onClose(false);
                    } else {
                        localService.toastify(res.info, 'error', 1000);
                    }
                    setLoading(false);
                })
                .catch(() => {
                    setLoading(false);
                    localService.toastify('Error uploading file', 'error', 1000);
                });
        }
    };

    // Handle file selection
    const getImageHandle = (e: any) => {
        if (e.target.files && e.target.files.length > 0) {
            const selectedFile = e.target.files[0];
            const fileType = selectedFile.type;
            const fileSize = selectedFile.size / 1024; // in KB
            const maxSize = fileSizeLimits[fileType] || 0;

            const isImage = fileType.startsWith('image/');

            if (fileSize < fileMinimumSize || fileSize > maxSize) {
                setError({
                    status: true,
                    message: `File size must not exceed ${maxSize}KB for this file type.`,
                });
                return;
            }

            if (isImage && coverImage) {
                const img = new Image();
                img.src = URL.createObjectURL(selectedFile);
                img.onload = () => {
                    const { width, height } = img;

                    if (width < 800) {
                        setError({
                            status: true,
                            message: 'Image must be at least 800px wide.',
                        });
                        return;
                    }

                    const targetAspect = 4 / 3;
                    const imgAspect = width / height;

                    let sx = 0, sy = 0, sWidth = width, sHeight = height;

                    // Crop horizontally or vertically to fit 4:3
                    if (imgAspect > targetAspect) {
                        // Wider than 4:3 — crop sides
                        sWidth = height * targetAspect;
                        sx = (width - sWidth) / 2;
                    } else {
                        // Taller than 4:3 — crop top/bottom
                        sHeight = width / targetAspect;
                        sy = (height - sHeight) / 2;
                    }

                    const canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = width * 3 / 4; // force 4:3

                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                        ctx.drawImage(img, sx, sy, sWidth, sHeight, 0, 0, canvas.width, canvas.height);

                        canvas.toBlob((blob) => {
                            if (blob) {
                                const croppedFile = new File([blob], selectedFile.name, { type: selectedFile.type });
                                setFile(croppedFile);
                                setSelectedImage(URL.createObjectURL(croppedFile));
                                setError({ status: false, message: '' });
                            }
                        }, fileType);
                    }
                };
            } else {
                setFile(selectedFile);
                setSelectedImage(URL.createObjectURL(selectedFile));
                setError({ status: false, message: '' });
            }
        }
    };
    const [crop, setCrop] = useState({ x: 0, y: 0 })
    const [zoom, setZoom] = useState(1)


    const [croppedBlob, setCroppedBlob] = useState(null);


    const getCroppedImg = async (imageSrc: any, croppedAreaPixels: any) => {
        const image = new Image();
        image.src = imageSrc;
        await new Promise((resolve) => { image.onload = resolve; });

        const canvas = document.createElement('canvas');
        const outputWidth = 1280;
        const outputHeight = 960;

        canvas.width = outputWidth;
        canvas.height = outputHeight;
        const ctx: any = canvas.getContext('2d');

        ctx.drawImage(
            image,
            croppedAreaPixels.x,
            croppedAreaPixels.y,
            croppedAreaPixels.width,
            croppedAreaPixels.height,
            0,
            0,
            outputWidth,
            outputHeight
        );
        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                resolve(blob);
            }, 'image/jpeg');
        });
    };
    const [croppedAreaPixels, setCroppedAreaPixels] = useState()
    const onCropComplete = useCallback(async (croppedArea: any, croppedAreaPixels: any) => {
        setCroppedAreaPixels(croppedAreaPixels); // store this in state
    }, []);
    const handleCropAndUpload = async () => {
        if (file && croppedAreaPixels) {
            const croppedImageBlob: any = await getCroppedImg(URL.createObjectURL(file), croppedAreaPixels);
            setCroppedBlob(croppedImageBlob);

            const croppedFile = new File([croppedImageBlob], file.name, { type: 'image/jpeg' });

            const form = new FormData();
            form.append('file', croppedFile, file.name);
            form.append('path', path);

            try {
                const res = await UploadService.UploadMedia(form);

                if (res.status === gtUtil.Constants.SUCCESS) {
                    const uploadedUrl = res?.data?.rows?.url;
                    const processedData = res?.data?.rows?.processed;
                    onSend({
                        url: uploadedUrl,
                        key: res?.data?.rows?.key,
                        processed: processedData
                    });

                    localService.toastify("Cropped image uploaded successfully!", 'success', 1000);
                    onClose(false);

                } else {
                    localService.toastify(res.info, 'error', 1000);
                }
            } catch (error) {
                localService.toastify('Error uploading cropped image', 'error', 1000);
            }
        }
    };


    return (
        <CommonPopUp
            modalTitle="Upload"
            modalContent=""
            openModal={openModal}
            onClose={() => onClose(false)}
            className="modal-medium mx-auto"
        >
            <div className="upload-doc-sec">
                <Fragment>
                    {/* {file && (
                        <div className="creative-image-preview-sec">
                            <span
                                className="material-symbols-rounded icon-filled close-preview"
                                onClick={handleClosePreviewImage}
                            >
                                cancel
                            </span>
                            {(file.type !== 'application/pdf' &&
                                file.type !== 'application/msword' &&
                                file.type !== 'video/mp4') && (
                                <>
                                 <Cropper
                                    image={URL.createObjectURL(file)}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={4 / 3}
                                    onCropChange={setCrop}
                                    onCropComplete={onCropComplete}
                                    onZoomChange={setZoom}
                                    
                                />
                                <img
                                    src={URL.createObjectURL(file)}
                                    alt=""
                                    className="creative-image-preview"
                                />
                                </>
                            )}
                            {file.type === 'video/mp4' && (
                                <video
                                    style={{ cursor: 'pointer' }}
                                    height={400}
                                    width={400}
                                    className="creative-image-preview"
                                    controls
                                >
                                    <source src={URL.createObjectURL(file)} type="video/mp4" />
                                </video>
                            )}
                            {(file.type === 'application/pdf') &&
                                <MaterialSymbol fontSize='48px' iconName='picture_as_pdf' fontweight='200' />
                                }
                                {(
                                file.type === 'application/msword' ||
                                file.type === 'mats-officedocument.wordprocessingml.document') &&
                                <MaterialSymbol fontSize='48px' iconName='description' fontweight='200' />
                                }
                        </div>
                    )} */}
                    {file && coverImage && (
                        <div className="creative-image-preview-sec">
                            <span
                                className="material-symbols-rounded icon-filled close-preview"
                                onClick={handleClosePreviewImage}
                            >
                                cancel
                            </span>
                            {(file.type !== 'application/pdf' &&
                                file.type !== 'application/msword' &&
                                file.type !== 'video/mp4') && (
                                    <>
                                        <Cropper
                                            image={URL.createObjectURL(file)}
                                            crop={crop}
                                            zoom={zoom}
                                            aspect={4 / 3}
                                            onCropChange={setCrop}
                                            onCropComplete={onCropComplete}
                                            onZoomChange={setZoom}
                                        />

                                    </>
                                )}

                        </div>
                    )}



                    {file && !coverImage && (
                        <div className="creative-image-preview-sec">
                            <span
                                className="material-symbols-rounded icon-filled close-preview"
                                onClick={handleClosePreviewImage}
                            >
                                cancel
                            </span>
                            {(file.type !== 'application/pdf' &&
                                file.type !== 'application/msword' &&
                                file.type !== 'video/mp4') && (
                                    <img
                                        src={URL.createObjectURL(file)}
                                        alt=""
                                        className="creative-image-preview"
                                    />
                                )}
                            {file.type === 'video/mp4' && (
                                <video
                                    style={{ cursor: 'pointer' }}
                                    height={400}
                                    width={400}
                                    className="creative-image-preview"
                                    controls
                                >
                                    <source src={URL.createObjectURL(file)} type="video/mp4" />
                                </video>
                            )}
                            {(file.type === 'application/pdf') &&
                                <MaterialSymbol fontSize='48px' iconName='picture_as_pdf' fontweight='200' />
                            }
                            {(file.type === 'application/msword' ||
                                file.type === 'mats-officedocument.wordprocessingml.document') &&
                                <MaterialSymbol fontSize='48px' iconName='description' fontweight='200' />
                            }
                        </div>
                    )}
                    {!file && (
                        <div className="upload-btn">
                            <input type="file" accept={acceptedFileTypes} onChange={getImageHandle} />
                            <span className="material-symbols-rounded">upload</span>{' '}
                            <strong>Choose File</strong>
                        </div>
                    )}
                    {error.status && <span className="error">{error.message}</span>}

                    <span className="fs-14">accepts: {acceptedFileTypes}</span>
                    {coverImage && <span className='fs-14 d-block'>{coverImageDimensionText}</span>}
                    {!file && (
                        <>{(acceptedFileTypes === '.jpg, .jpeg, .png') &&
                            <span className='fs-14 pb-5 text-center'>
                                size limit : 2 MB</span>}

                            {(acceptedFileTypes === '.doc, .docx, .pdf') &&
                                <span className='fs-14 pb-5'>
                                    size limit : 500 KB.
                                </span>
                            }
                            {(acceptedFileTypes === '.mp4') &&
                                <span className='fs-14 pb-5'>size limit : 150 MB.</span>}
                        </>
                    )}
                    {file && (

                        <span className="fs-14 pb-5">
                            {/* {((file.type !== 'application/pdf')  &&  (file.type !== 
                        'application/msword') &&
        (file.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')) && <>
                            size limit : {((file.type !== 'application/pdf')  &&  (file.type !== 
                        'application/msword') &&
        (file.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')) && methods.decimalFormat((fileSizeLimits[file.type]) / 1024)} MB.</>} */}
                            {
                                // Check if file type is jpeg, png, or jpg
                                (file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg') && (
                                    <>
                                        size limit: {fileSizeLimits[file.type] ? methods.decimalFormat(fileSizeLimits[file.type] / 1024) : 'N/A'} MB.
                                    </>
                                )
                            }
                            {file?.type !== 'image/jpeg' && file?.type !== 'image/png' && file?.type !== 'image/jpg' && (
                                <>
                                    size limit : {(file.type === 'application/pdf' || file.type === 'application/msword' || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') && <>500 KB.</>
                                    }</>)
                            }

                        </span>
                    )}
                    {loading && (
                        <div className="uploading-progress">
                            <LinearProgress />
                            <span className="fs-14">File is uploading, please don't close this window.</span>
                        </div>
                    )}
                    {coverImage && (
                        <>
                            {(file && !loading) && 
                                <p className='fs-14 pt-2 pb-2 mb-0'>Use the mouse wheel or touchpad to adjust image size</p>
                            }
                            <button disabled={!file || loading} className={`upload-file-btn btn btn-primary ${(!file || loading) ? 'disabled' : ''}`} onClick={handleCropAndUpload}>{(!file || loading) ? "Upload" : "Apply Crop & Upload"}</button>
                        </>
                    )}

                    {!coverImage && (
                        <button
                            className={`upload-file-btn btn btn-primary ${(!file || loading) ? 'disabled' : ''}`}
                            disabled={!file || loading}
                            onClick={uploadFile}
                        >
                            {loading ? 'Uploading...' : 'Upload'}
                        </button>
                    )}
                </Fragment>
            </div>
        </CommonPopUp>
    );
};

export default FileUploadPopup;
